import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import { Offcanvas, Spinner, Form, Button } from 'react-bootstrap';
import { postMensajeMTeam, deleteResetMTeam } from '../../api/AgenteMTeamApi';
import AuthContext from '../../contexts/AuthContext';

interface Mensaje {
    rol: 'user' | 'assistant';
    texto: string;
}

const FRASES_ALEATORIAS = [
    '¿Necesitas ver el estado de una OP? ¡Pregúntame!',
    'Tengo datos de planificaciones. ¿Qué necesitas saber?',
    '¿Buscas el informe técnico de un componente?',
    'Puedo mostrarte el estado de cualquier reparación.',
    '¿Cuántas OPs están en evaluación hoy?',
    '¿Quieres ver las últimas planificaciones activas?',
    'Pregúntame por cualquier orden de proceso. 📋',
];

const AgenteMTeamChat = (): React.ReactElement => {
    const { userName } = useContext(AuthContext);
    const primerNombre = userName?.split(' ')[0] || 'tú';

    const [visible, setVisible] = useState(true);
    const [showChat, setShowChat] = useState(false);
    const [inputText, setInputText] = useState('');
    const [mensajes, setMensajes] = useState<Mensaje[]>([]);
    const [cargando, setCargando] = useState(false);
    const [reseteando, setReseteando] = useState(false);
    const [fraseActual, setFraseActual] = useState(`¡Hola <b>${primerNombre}</b>! Soy <b>MTeam</b> 🤖`);
    const [burbujaVisible, setBurbujaVisible] = useState(true);

    const chatBodyRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const mostrarFrase = useCallback((texto: string) => {
        setFraseActual(texto);
        setBurbujaVisible(true);
        if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
        fadeTimerRef.current = setTimeout(() => setBurbujaVisible(false), 6000);
    }, []);

    const iniciarCicloFrases = useCallback(() => {
        mostrarFrase(`¡Hola <b>${primerNombre}</b>! Soy <b>MTeam</b> 🤖`);
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            const frase = FRASES_ALEATORIAS[Math.floor(Math.random() * FRASES_ALEATORIAS.length)];
            mostrarFrase(frase);
        }, 15000);
    }, [mostrarFrase, primerNombre]);

    useEffect(() => {
        iniciarCicloFrases();
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
        };
    }, [iniciarCicloFrases]);

    useEffect(() => {
        if (chatBodyRef.current) {
            chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
        }
    }, [mensajes, cargando]);

    const abrirChat = () => {
        setShowChat(true);
        setVisible(false);
        if (timerRef.current) clearInterval(timerRef.current);
        if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
        setTimeout(() => inputRef.current?.focus(), 300);
    };

    const cerrarChat = () => {
        setShowChat(false);
        setVisible(true);
        iniciarCicloFrases();
    };

    const enviarMensaje = async () => {
        const texto = inputText.trim();
        if (!texto || cargando) return;
        setInputText('');
        setMensajes(prev => [...prev, { rol: 'user', texto }]);
        setCargando(true);
        try {
            const res = await postMensajeMTeam(texto);
            setMensajes(prev => [...prev, { rol: 'assistant', texto: res.data.reply }]);
        } catch (err: any) {
            const reply = err?.response?.data?.reply || 'Error de conexión. Por favor intenta nuevamente.';
            setMensajes(prev => [...prev, { rol: 'assistant', texto: reply }]);
        } finally {
            setCargando(false);
        }
    };

    const limpiarConversacion = async () => {
        if (reseteando) return;
        setReseteando(true);
        try {
            await deleteResetMTeam();
            setMensajes([]);
        } catch {
            // silencioso: si falla el reset en el servidor igual limpia la vista
            setMensajes([]);
        } finally {
            setReseteando(false);
        }
    };

    const renderTexto = (texto: string): string =>
        texto.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>').replace(/\n/g, '<br>');

    return (
        <>
            {visible && (
                <div style={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: 9990 }}>
                    {burbujaVisible && (
                        <div style={{
                            position: 'absolute',
                            bottom: '148px',
                            right: '8px',
                            background: 'white',
                            border: '1px solid #dee2e6',
                            borderRadius: '12px',
                            padding: '8px 12px',
                            maxWidth: '220px',
                            boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
                            fontSize: '0.78rem',
                            lineHeight: '1.4',
                            transition: 'opacity 0.4s',
                        }}
                            dangerouslySetInnerHTML={{ __html: fraseActual }}
                        />
                    )}

                    <button
                        onClick={() => setVisible(false)}
                        title="Ocultar asistente"
                        style={{
                            position: 'absolute',
                            top: '-6px',
                            left: '-6px',
                            zIndex: 10001,
                            background: '#212529',
                            border: '2px solid white',
                            borderRadius: '50%',
                            width: '22px',
                            height: '22px',
                            color: 'white',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '10px',
                            lineHeight: 1,
                            padding: 0,
                        }}
                    >✕</button>

                    <img
                        src="/mteam_saludo.png"
                        alt="MTeam Asistente"
                        onClick={abrirChat}
                        style={{
                            width: '130px',
                            height: 'auto',
                            cursor: 'pointer',
                            filter: 'drop-shadow(0px 5px 15px rgba(0,0,0,0.25))',
                            display: 'block',
                        }}
                    />
                </div>
            )}

            <Offcanvas
                show={showChat}
                onHide={cerrarChat}
                placement="end"
                style={{ width: '100%', maxWidth: '400px', borderLeft: '4px solid #198754' }}
            >
                {/* ── Header ── */}
                <Offcanvas.Header
                    style={{ background: 'linear-gradient(135deg, #198754 0%, #20c997 100%)', padding: '12px 16px' }}
                >
                    <div className="d-flex align-items-center" style={{ flex: 1 }}>
                        <img
                            src="/mteam_saludo.png"
                            width="62"
                            alt="MTeam"
                            style={{ marginRight: '10px', filter: 'drop-shadow(0px 2px 6px rgba(0,0,0,0.2))', transform: 'scaleX(-1)' }}
                        />
                        <div>
                            <Offcanvas.Title style={{ color: 'white', fontWeight: 700, marginBottom: 0, fontSize: '1rem' }}>
                                MTeam
                            </Offcanvas.Title>
                            <small style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.75rem' }}>Tu Asistente IA</small>
                        </div>

                        {/* Saludo con nombre de usuario */}
                        <div style={{
                            marginLeft: 'auto',
                            marginRight: '10px',
                            textAlign: 'right',
                        }}>
                            <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.7rem', lineHeight: 1 }}>Hola,</div>
                            <div style={{
                                color: 'white',
                                fontWeight: 700,
                                fontSize: '0.85rem',
                                maxWidth: '110px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}>{primerNombre}</div>
                        </div>
                    </div>
                    <button
                        className="btn-close btn-close-white"
                        onClick={cerrarChat}
                        aria-label="Cerrar"
                    />
                </Offcanvas.Header>

                <Offcanvas.Body
                    className="bg-light"
                    style={{ display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}
                >
                    {/* ── Cuerpo de mensajes ── */}
                    <div
                        ref={chatBodyRef}
                        style={{ flex: 1, overflowY: 'auto', padding: '16px', scrollBehavior: 'smooth' }}
                    >
                        {/* Burbuja de bienvenida */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginBottom: '16px' }}>
                            <div style={{
                                background: 'white',
                                border: '1px solid #dee2e6',
                                borderRadius: '12px',
                                borderBottomLeftRadius: 0,
                                padding: '12px 16px',
                                maxWidth: '90%',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                                fontSize: '0.875rem',
                            }}>
                                👋 <b>¡Hola, {primerNombre}!</b><br />
                                Soy el asistente digital del ERP de Electrans. Pregúntame sobre:
                                <ul style={{ marginBottom: 0, paddingLeft: '20px', marginTop: '8px', color: '#6c757d', fontSize: '0.82rem' }}>
                                    <li>📋 Órdenes de Proceso (OPs)</li>
                                    <li>📅 Planificaciones</li>
                                    <li>🔧 Reparaciones</li>
                                    <li>📄 Informes Técnicos</li>
                                </ul>
                            </div>
                        </div>

                        {mensajes.map((m, i) => (
                            <div
                                key={i}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: m.rol === 'user' ? 'flex-end' : 'flex-start',
                                    marginBottom: '12px',
                                }}
                            >
                                {m.rol === 'user' && (
                                    <small style={{ color: '#adb5bd', fontSize: '0.7rem', marginBottom: '2px', marginRight: '4px' }}>
                                        {primerNombre}
                                    </small>
                                )}
                                <div
                                    style={{
                                        maxWidth: '90%',
                                        padding: '10px 16px',
                                        borderRadius: '12px',
                                        borderBottomRightRadius: m.rol === 'user' ? 0 : '12px',
                                        borderBottomLeftRadius: m.rol === 'assistant' ? 0 : '12px',
                                        backgroundColor: m.rol === 'user' ? '#198754' : 'white',
                                        color: m.rol === 'user' ? 'white' : '#212529',
                                        border: m.rol === 'assistant' ? '1px solid #dee2e6' : 'none',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                                        fontSize: '0.875rem',
                                        lineHeight: '1.5',
                                    }}
                                    dangerouslySetInnerHTML={{ __html: renderTexto(m.texto) }}
                                />
                            </div>
                        ))}

                        {cargando && (
                            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
                                <div style={{
                                    background: 'white',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '12px',
                                    borderBottomLeftRadius: 0,
                                    padding: '10px 16px',
                                    maxWidth: '90%',
                                    color: '#6c757d',
                                    fontStyle: 'italic',
                                    fontSize: '0.875rem',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                                }}>
                                    <Spinner animation="border" size="sm" variant="success" style={{ marginRight: '8px' }} />
                                    Procesando...
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ── Input y footer ── */}
                    <div style={{ padding: '12px 16px', background: 'white', borderTop: '1px solid #dee2e6' }}>
                        <div style={{ display: 'flex', gap: '0' }}>
                            <Form.Control
                                ref={inputRef as any}
                                type="text"
                                value={inputText}
                                onChange={e => setInputText(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && enviarMensaje()}
                                placeholder="Escribe tu pregunta..."
                                disabled={cargando}
                                style={{ borderRadius: '25px 0 0 25px', boxShadow: 'none', fontSize: '0.875rem' }}
                            />
                            <Button
                                variant="success"
                                onClick={enviarMensaje}
                                disabled={cargando || !inputText.trim()}
                                style={{ borderRadius: '0 25px 25px 0', padding: '8px 18px' }}
                                title="Enviar"
                            >
                                ✈
                            </Button>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '6px' }}>
                            <small style={{ color: '#adb5bd', fontSize: '0.68rem' }}>MTeam v1.0 · ERP Electrans</small>
                            <button
                                onClick={limpiarConversacion}
                                disabled={reseteando || mensajes.length === 0}
                                title="Limpiar conversación"
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: mensajes.length === 0 ? 'default' : 'pointer',
                                    color: mensajes.length === 0 ? '#dee2e6' : '#adb5bd',
                                    fontSize: '0.72rem',
                                    padding: '0 2px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '3px',
                                    transition: 'color 0.2s',
                                }}
                                onMouseEnter={e => { if (mensajes.length > 0) (e.currentTarget as HTMLElement).style.color = '#dc3545'; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = mensajes.length === 0 ? '#dee2e6' : '#adb5bd'; }}
                            >
                                {reseteando ? <Spinner animation="border" size="sm" /> : '🗑'} Nueva conversación
                            </button>
                        </div>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
};

export default AgenteMTeamChat;
